import React, { useState, useEffect } from 'react';
import { KoreanTextModel, BookModel, QuestionModel } from '../../../../models';
import { db } from '../../../../firebase';
import { doc, updateDoc, deleteDoc, getDocs, collection, query, where, writeBatch, arrayRemove, arrayUnion } from 'firebase/firestore';
import { COLLECTIONS } from '../../../../models/collectionNames';
import { Loader2, Plus, AlertCircle, Trash2 } from 'lucide-react';
import QuestionTile from './QuestionTile';

interface PracticeQuestionsProps {
    unit: KoreanTextModel;
    bookId: string;
    activeBook: BookModel;
    onUpdate: (updatedFields: Partial<KoreanTextModel>) => void;
    draftPassage?: string;
    draftGenre?: string;
}

interface SetOption {
    id: string;
    createdAt: number;
    questionCount: number;
}

const PracticeQuestions: React.FC<PracticeQuestionsProps> = ({ 
    unit, 
    bookId, 
    activeBook, 
    onUpdate,
    draftPassage,
    draftGenre
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [questionSets, setQuestionSets] = useState<SetOption[]>([]);
    const [selectedSetId, setSelectedSetId] = useState<string>('');
    const [questions, setQuestions] = useState<QuestionModel[]>([]);

    const loadQuestionSets = async (targetSetId?: string) => {
        setIsLoading(true);
        try {
            const qSets = query(
                collection(db, COLLECTIONS.BOOK_QUESTION_SETS),
                where('paragraphId', '==', unit.id)
            );
            const snap = await getDocs(qSets);
            const setsList: SetOption[] = snap.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    createdAt: data.createdAt || 0,
                    questionCount: data.questionIds?.length || 0
                };
            });

            // Sort by creation date descending
            setsList.sort((a, b) => b.createdAt - a.createdAt);
            setQuestionSets(setsList);

            if (setsList.length > 0) {
                const activeId = targetSetId && setsList.some(s => s.id === targetSetId)
                    ? targetSetId
                    : setsList[0].id;
                setSelectedSetId(activeId);
                await loadQuestions(activeId);
            } else {
                setSelectedSetId('');
                setQuestions([]);
            }
        } catch (err: any) {
            console.error('Failed to load question sets:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const loadQuestions = async (setId: string) => {
        try {
            const q = query(
                collection(db, COLLECTIONS.BOOK_QUESTIONS),
                where('setId', '==', setId)
            );
            const qSnap = await getDocs(q);
            const list = qSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as any as QuestionModel));
            list.sort((a, b) => (a.number || 0) - (b.number || 0));
            setQuestions(list);
        } catch (err: any) {
            console.error('Failed to load questions:', err);
        }
    };

    useEffect(() => {
        loadQuestionSets();
    }, [unit.id]);

    const handleSetChange = async (setId: string) => {
        setSelectedSetId(setId);
        await loadQuestions(setId);
    };

    const handleDeleteQuestionSet = async () => {
        if (!selectedSetId) return;
        if (!window.confirm('이 문항 세트와 포함된 모든 문제를 정말로 삭제하시겠습니까?')) return;
        
        setIsLoading(true);
        try {
            const batch = writeBatch(db);
            
            // 1. Delete all questions in the set
            const qQuery = query(
                collection(db, COLLECTIONS.BOOK_QUESTIONS),
                where('setId', '==', selectedSetId)
            );
            const qSnap = await getDocs(qQuery);
            qSnap.docs.forEach(docSnap => {
                batch.delete(doc(db, COLLECTIONS.BOOK_QUESTIONS, docSnap.id));
            });
            
            // 2. Delete the question set itself
            batch.delete(doc(db, COLLECTIONS.BOOK_QUESTION_SETS, selectedSetId));
            
            // 3. Remove from unit's questionSetIds array
            batch.update(doc(db, COLLECTIONS.BOOK_UNIT, unit.id), {
                questionSetIds: arrayRemove(selectedSetId)
            });
            
            await batch.commit();
            
            alert('문항 세트가 성공적으로 삭제되었습니다.');
            
            // 4. Reload question sets
            await loadQuestionSets();
        } catch (err: any) {
            alert('문항 세트 삭제 실패: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateInitialSetAndQuestion = async () => {
        const timestamp = Date.now();
        const setRef = doc(collection(db, COLLECTIONS.BOOK_QUESTION_SETS));
        const setId = setRef.id;
        const qId = `${setId}_q_${timestamp}`;
        const newQ: QuestionModel = {
            id: qId,
            number: 1,
            msType: 'multiple',
            questionHtml: '<p>새로운 문제를 입력해 주세요.</p>',
            answer: '1',
            options: ['보기 1', '보기 2', '보기 3', '보기 4', '보기 5'],
            explanationHtml: '<p>해설을 입력해 주세요.</p>'
        };

        try {
            const batch = writeBatch(db);
            batch.set(setRef, {
                id: setId,
                bookId,
                paragraphId: unit.id,
                questionIds: [qId],
                createdAt: timestamp
            });
            batch.set(doc(db, COLLECTIONS.BOOK_QUESTIONS, qId), {
                ...newQ,
                paragraphId: unit.id,
                setId: setId,
                updatedAt: timestamp
            });
            batch.update(doc(db, COLLECTIONS.BOOK_UNIT, unit.id), {
                questionSetIds: arrayUnion(setId),
                "_stats.questionsCount": 1,
                updatedAt: timestamp
            });
            await batch.commit();

            await loadQuestionSets(setId);
            onUpdate({
                questionSetIds: [...(unit.questionSetIds || []), setId],
                _stats: {
                    ...(unit._stats || { conceptDone: false, analysisDone: false, questionsCount: 0, updatedAt: Date.now() }),
                    questionsCount: 1,
                    updatedAt: Date.now()
                }
            });
            alert('새 문항 세트가 추가되었습니다.');
        } catch (err: any) {
            alert('문항 세트 추가 실패: ' + err.message);
        }
    };

    const handleEditQuestion = async (updatedQ: QuestionModel) => {
        try {
            const qRef = doc(db, COLLECTIONS.BOOK_QUESTIONS, updatedQ.id);
            await updateDoc(qRef, {
                msType: updatedQ.msType,
                questionHtml: updatedQ.questionHtml,
                answer: updatedQ.answer,
                explanationHtml: updatedQ.explanationHtml || '',
                options: updatedQ.options || [],
                updatedAt: Date.now()
            });
            setQuestions(prev => prev.map(item => item.id === updatedQ.id ? updatedQ : item));
            alert('문항이 수정되었습니다.');
        } catch (err: any) {
            alert('문항 수정 실패: ' + err.message);
        }
    };

    const handleDeleteQuestion = async (qId: string) => {
        if (!window.confirm('이 문제를 세트에서 정말 삭제하시겠습니까?')) return;
        try {
            await deleteDoc(doc(db, COLLECTIONS.BOOK_QUESTIONS, qId));

            const currentIds = questions.map(q => q.id).filter(id => id !== qId);

            const setRef = doc(db, COLLECTIONS.BOOK_QUESTION_SETS, selectedSetId);
            await updateDoc(setRef, {
                questionIds: currentIds
            });

            // Update local state count
            setQuestionSets(prev => prev.map(s => s.id === selectedSetId ? { ...s, questionCount: currentIds.length } : s));
            setQuestions(prev => prev.filter(item => item.id !== qId));

            const unitRef = doc(db, COLLECTIONS.BOOK_UNIT, unit.id);
            await updateDoc(unitRef, {
                "_stats.questionsCount": currentIds.length,
                updatedAt: Date.now()
            });

            onUpdate({
                _stats: {
                    ...(unit._stats || { conceptDone: false, analysisDone: false, questionsCount: 0, updatedAt: Date.now() }),
                    questionsCount: currentIds.length,
                    updatedAt: Date.now()
                }
            });
            alert('문항이 삭제되었습니다.');
        } catch (err: any) {
            alert('문항 삭제 실패: ' + err.message);
        }
    };

    const handleAddQuestion = async () => {
        if (!selectedSetId) {
            await handleCreateInitialSetAndQuestion();
            return;
        }
        const timestamp = Date.now();
        const nextNum = questions.length + 1;
        const qId = `${selectedSetId}_q_${timestamp}`;
        const newQ: QuestionModel = {
            id: qId,
            number: nextNum,
            msType: 'multiple',
            questionHtml: '<p>새로운 문제를 입력해 주세요.</p>',
            answer: '1',
            options: ['보기 1', '보기 2', '보기 3', '보기 4', '보기 5'],
            explanationHtml: '<p>해설을 입력해 주세요.</p>'
        };

        try {
            const batch = writeBatch(db);
            batch.set(doc(db, COLLECTIONS.BOOK_QUESTIONS, qId), {
                ...newQ,
                paragraphId: unit.id,
                setId: selectedSetId,
                updatedAt: timestamp
            });

            const currentIds = [...questions.map(q => q.id), qId];
            batch.update(doc(db, COLLECTIONS.BOOK_QUESTION_SETS, selectedSetId), {
                questionIds: currentIds
            });

            batch.update(doc(db, COLLECTIONS.BOOK_UNIT, unit.id), {
                "_stats.questionsCount": currentIds.length,
                updatedAt: timestamp
            });

            await batch.commit();

            setQuestions(prev => [...prev, newQ]);
            setQuestionSets(prev => prev.map(s => s.id === selectedSetId ? { ...s, questionCount: currentIds.length } : s));

            onUpdate({
                _stats: {
                    ...(unit._stats || { conceptDone: false, analysisDone: false, questionsCount: 0, updatedAt: Date.now() }),
                    questionsCount: currentIds.length,
                    updatedAt: Date.now()
                }
            });
            alert('새 문항이 생성되어 편집 상태로 추가되었습니다.');
        } catch (err: any) {
            alert('문항 추가 실패: ' + err.message);
        }
    };

    const hasSets = questionSets.length > 0;

    return (
        <div className="space-y-6 relative h-full flex flex-col overflow-hidden pb-4">
            {/* Header Toolbar */}
            <div className="flex items-center justify-between shrink-0 border-b border-slate-100 pb-3">
                <div className="flex items-center gap-3">
                    <span className="text-xs font-black text-slate-700">출제 문항 목록</span>
                    {hasSets && (
                        <div className="flex items-center gap-1.5">
                            <select
                                value={selectedSetId}
                                onChange={(e) => handleSetChange(e.target.value)}
                                className="px-2.5 py-1.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-700 rounded-lg text-[10px] font-black focus:outline-none cursor-pointer"
                            >
                                {questionSets.map((s, idx) => (
                                    <option key={s.id} value={s.id}>
                                        세트 {questionSets.length - idx} ({s.questionCount}문항) - {new Date(s.createdAt).toLocaleDateString()}
                                    </option>
                                ))}
                            </select>
                            <button
                                onClick={handleDeleteQuestionSet}
                                className="p-1.5 hover:bg-rose-50 text-slate-400 hover:text-rose-500 rounded-lg transition-all border border-transparent hover:border-rose-100 cursor-pointer"
                                title="이 문항 세트 전체 삭제"
                            >
                                <Trash2 size={12} />
                            </button>
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={handleAddQuestion}
                        className="flex items-center gap-1 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-[10px] font-black transition-colors shadow-sm"
                    >
                        <Plus size={11} />
                        <span>문항 수동 추가</span>
                    </button>
                </div>
            </div>

            {/* Content Body */}
            <div className="flex-1 overflow-y-auto pr-1 space-y-4 custom-scrollbar">
                {isLoading ? (
                    <div className="flex flex-col items-center justify-center py-20 space-y-3">
                        <Loader2 className="animate-spin text-indigo-600" size={24} />
                        <span className="text-xs font-bold text-slate-500">문항 목록 로딩 중...</span>
                    </div>
                ) : !hasSets ? (
                    <div className="flex flex-col items-center justify-center py-16 border-2 border-dashed border-slate-200 rounded-2xl space-y-4 bg-slate-50/50">
                        <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center text-slate-400">
                            <AlertCircle size={22} />
                        </div>
                        <div className="text-center">
                            <h4 className="text-xs font-black text-slate-800">생성된 실전문항 세트가 없습니다</h4>
                            <p className="text-[10px] text-slate-400 font-bold mt-1 leading-relaxed">
                                아래 버튼을 눌러 새 문항 세트를 직접 생성하고 문제를 추가하실 수 있습니다.
                            </p>
                        </div>
                        <button
                            onClick={handleCreateInitialSetAndQuestion}
                            className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-black shadow-md transition-all active:scale-95"
                        >
                            <Plus size={12} />
                            <span>새 문항 세트 추가</span>
                        </button>
                    </div>
                ) : questions.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center text-slate-400 space-y-2 bg-slate-50/50 rounded-xl border border-slate-100">
                        <span className="text-xs font-bold">이 세트에는 포함된 문항이 없습니다.</span>
                        <button
                            onClick={handleAddQuestion}
                            className="px-3 py-1.5 bg-indigo-600 text-white font-black text-[10px] rounded-lg shadow-sm hover:bg-indigo-700"
                        >
                            첫 번째 문항 추가
                        </button>
                    </div>
                ) : (
                    <div className="space-y-5">
                        {questions.map((q, idx) => (
                            <QuestionTile
                                key={q.id || idx}
                                question={q}
                                index={idx}
                                onEdit={handleEditQuestion}
                                onDelete={() => handleDeleteQuestion(q.id)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PracticeQuestions;
